import React, { useState, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { apiClient } from '../utils/api';
import { industries, companySizes, roles } from '../utils/constants';
import './SettingsPage.css';

export default function SettingsPage({ company, onUpdate, onLogout, showToast }) {
  const initialForm = {
    company_name: company.company_name || company.name || '',
    industry: company.industry || '',
    company_size: company.company_size || '',
    role: company.role || '',
    phone: company.phone || '',
    website: company.website || '',
  };
  
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const dirty = Object.keys(initialForm).some(k => form[k] !== initialForm[k]);
    setIsDirty(dirty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const update = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await apiClient.updateCompany(company.id, form);
      if (data.success || data.company) {
        onUpdate(data.company || { ...company, ...form });
        showToast('Settings saved successfully! 🎉');
        setIsDirty(false);
      } else {
        showToast('Failed to save: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (err) {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This will permanently delete ALL your tests, bug reports, and data. This cannot be undone.'
    );
    if (!confirmed) return;

    const doubleConfirm = window.confirm('FINAL CONFIRMATION: Are you absolutely sure?');
    if (!doubleConfirm) return;

    try {
      await apiClient.deleteCompany(company.id);
      showToast('Account deleted');
      onLogout();
    } catch (err) {
      showToast('Failed to delete account', 'error');
    }
  };

  return (
    <div className="settings-page">
      <header className="sp-header-container">
        <h1>Settings</h1>
      </header>

      <div className="sp-grid">
        <div className="sp-col-left">
          <div className="sp-main-card">
            <div className="sp-section-label">Profile details</div>
            <div className="sp-profile-row">
              {company.picture ? (
                <img src={company.picture} alt="" className="sp-avatar" />
              ) : (
                <div className="sp-avatar-placeholder">
                  {company.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="sp-profile-info">
                <div className="sp-profile-name">{company.name}</div>
                <div className="sp-profile-email">{company.email}</div>
                <div className="sp-profile-badge">Administrator</div>
              </div>
            </div>

            <div className="sp-form">
            <div className="sp-form-row full-width">
              <div className="form-group">
                <label>Company name</label>
                <input
                  type="text"
                  className="sp-input"
                  value={form.company_name}
                  onChange={e => update('company_name', e.target.value)}
                />
              </div>
            </div>

            <div className="sp-form-row">
              <div className="form-group">
                <label>Industry</label>
                <div className="select-wrapper">
                  <select
                    className="sp-input"
                    value={form.industry}
                    onChange={e => update('industry', e.target.value)}
                  >
                    <option value="">Select Industry</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Company size</label>
                <div className="select-wrapper">
                  <select
                    className="sp-input"
                    value={form.company_size}
                    onChange={e => update('company_size', e.target.value)}
                  >
                    <option value="">Select Size</option>
                    {companySizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="sp-form-row">
              <div className="form-group">
                <label>Your role</label>
                <div className="select-wrapper">
                  <select
                    className="sp-input"
                    value={form.role}
                    onChange={e => update('role', e.target.value)}
                  >
                    <option value="">Select Role</option>
                    {roles.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Phone <span className="sp-optional">(optional)</span></label>
                <input
                  type="tel"
                  className="sp-input"
                  value={form.phone}
                  onChange={e => update('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div className="sp-form-row full-width">
              <div className="form-group">
                <label>Website <span className="sp-optional">(optional)</span></label>
                <input
                  type="url"
                  className="sp-input"
                  value={form.website}
                  onChange={e => update('website', e.target.value)}
                  placeholder="https://yourcompany.com"
                />
              </div>
            </div>

            <div className="sp-actions">
              {isDirty && (
                <div className="sp-unsaved">
                  <span className="sp-unsaved-dot"></span>
                  Unsaved changes
                </div>
              )}
              <button
                className="sp-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
        </div>

        <div className="sp-col-right">
          <div className="sp-card">
            <h2 className="sp-card-title">Session</h2>
            <p className="sp-card-desc">Your data is auto-saved.</p>
            <button className="sp-ghost-btn" onClick={onLogout}>
              <LogOut size={16} />
              <span>Sign Out Securely</span>
            </button>
          </div>

          <div className="sp-card danger-card">
            <h2 className="sp-card-title text-red">Danger zone</h2>
            <p className="sp-card-desc">
              Deleting your account is permanent. All tests, reports, and team data will be erased immediately.
            </p>
            <button className="sp-delete-btn" onClick={handleDeleteAccount}>
              Delete account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}